// Pago "de paso" por useredy.com para la página servida FUERA de useredy.com:
// un «Dominio propio» (empanadafactorytx.com) o un host consumer (redytoeat.com,
// dallasvibra.com).
//
// El Embedded Checkout de North está amarrado al dominio registrado en North
// (hoy uno solo: useredy.com, un solo NORTH_CHECKOUT_ID). Registrar dominio por
// dominio y un checkout por merchant no escala, así que fuera de useredy.com:
//
//   1. el cliente arma el carrito y llena sus datos en empanadafactorytx.com;
//   2. al pagar, PaymentStep crea la sesión (/api/online-orders/session guarda
//      el pedido COMPLETO en online_checkout_intents) y el navegador sale a
//      useredy.com/order/<slug>/pagar#t=<token>, donde North sí monta;
//   3. al aprobarse, /complete (o /intent-status) crea la orden desde el intent
//      — el mismo camino que ya rescata los pagos con Apple Pay;
//   4. el cliente vuelve al host del que salió (https://<dominio>/ o
//      https://redytoeat.com/<slug>) con #pago=<token> y ve la confirmación de
//      siempre. Si cancela, vuelve con #pago-cancelado y su carrito intacto.
//      El host de vuelta viaja en el fragmento (`o=`) pero la página de pago lo
//      valida contra una lista cerrada (resolvePayReturnBase): nunca redirige a
//      un host arbitrario.
//
// El token viaja en el FRAGMENTO (#), nunca en la query: el navegador no lo
// manda al servidor, así que no queda en logs ni en Referer.
//
// Puro salvo el acceso a sessionStorage (con try/catch: modo privado, WebView).
import type { CartItem } from "@/components/microsite/types"
import type { ConfirmedOrder } from "@/components/microsite/sheet/OrderSheet"
import type { WelcomeCoupon } from "@/lib/marketing/welcomeCouponWire"
import { orderPaymentReference } from "@/lib/orders/orderNumber"
import { isMerchantPrimaryHost, MERCHANT_BASE } from "@/lib/site/customDomainHost"
import { consumerStorefrontReturnUrl } from "@/lib/site/consumerHost"
import { isSubstitutionPref, type SubstitutionPref } from "@/lib/online-orders/substitutionPrefs"
import { parseConversion } from "@/lib/tracking/conversionValue"
import { isSafeInternalPath } from "@/lib/capacitor/nav"

/**
 * ¿Hay que pagar por useredy.com? En todo host que no sea la casa merchant
 * (useredy.com, previews, localhost): dominios propios Y hosts consumer. Ahí
 * North no monta.
 */
export function shouldHandOffPayment(hostname: string): boolean {
  return !isMerchantPrimaryHost(hostname)
}

/** Página de pago en useredy.com para esta sesión, recordando de qué host salió. */
export function hostedPaymentUrl(slug: string, token: string, fromHost: string, base: string = MERCHANT_BASE): string {
  const hash = new URLSearchParams({ t: token, o: fromHost.split(":")[0].toLowerCase() })
  return `${base}/order/${encodeURIComponent(slug)}/pagar#${hash.toString()}`
}

/**
 * Pago de la DIFERENCIA de una sustitución (spec 2026-10-01 §4.3): /complete
 * responde `substitution: { outcome, redirect }` y no hay orden que confirmar.
 * Devuelve a dónde vuelve el cliente (su seguimiento `/p/<código>`; la tarjeta
 * explica qué pasó, también si se anuló o fue a revisión), o null si la
 * respuesta no es de una sustitución. `redirect` lo arma el servidor, pero solo
 * se acepta una URL https absoluta o una ruta propia; si no, `fallback`.
 */
export function substitutionRedirectFrom(data: unknown, fallback: string): string | null {
  if (!data || typeof data !== "object") return null
  const sub = (data as { substitution?: unknown }).substitution
  if (!sub || typeof sub !== "object") return null
  const redirect = (sub as { redirect?: unknown }).redirect
  if (typeof redirect === "string") {
    if (/^https:\/\/[^/\s]/i.test(redirect)) return redirect
    if (isSafeInternalPath(redirect)) return redirect
  }
  return fallback
}

/** Lo que trae la página de pago en su fragmento (`#t=…&o=…`). */
export function parsePayHash(hash: string): { token: string | null; fromHost: string | null } {
  const q = new URLSearchParams(hash.replace(/^#/, ""))
  const t = q.get("t")
  const o = q.get("o")
  return { token: t && t.length >= 8 ? t : null, fromHost: o || null }
}

/** Token que trae la página de pago en su fragmento, o null. */
export function tokenFromPayHash(hash: string): string | null {
  return parsePayHash(hash).token
}

/**
 * A qué página vuelve el cliente, SOLO entre destinos conocidos:
 *   - el dominio propio del local (con o sin www) → https://<dominio>/;
 *   - un host consumer que sirve este negocio → su storefront ahí;
 *   - si no, el dominio propio o, sin él, la tienda en useredy.com.
 * `fromHost` sale del fragmento (lo controla quien arma el link): por eso no se
 * usa nunca tal cual.
 */
export function resolvePayReturnBase(opts: {
  slug: string
  ownDomain: string | null
  fromHost: string | null
}): string {
  const { slug, ownDomain } = opts
  const from = (opts.fromHost ?? "").split(":")[0].toLowerCase()
  if (ownDomain && (from === ownDomain || from === `www.${ownDomain}`)) return `https://${ownDomain}/`
  const consumer = from ? consumerStorefrontReturnUrl(from, slug) : null
  if (consumer) return consumer
  return ownDomain ? `https://${ownDomain}/` : `/order/${slug}`
}

/** A dónde vuelve el cliente después de pagar (o cancelar). */
export function paymentReturnUrl(
  returnBase: string,
  outcome: { kind: "paid"; token: string } | { kind: "cancelled" }
): string {
  const base = returnBase.replace(/#.*$/, "")
  return outcome.kind === "paid"
    ? `${base}#pago=${encodeURIComponent(outcome.token)}`
    : `${base}#pago-cancelado`
}

export type PaymentReturn = { kind: "paid"; token: string } | { kind: "cancelled" } | null

/** Lee el fragmento con el que vuelve el cliente a su dominio. */
export function parsePaymentReturn(hash: string): PaymentReturn {
  const h = hash.replace(/^#/, "")
  if (h === "pago-cancelado") return { kind: "cancelled" }
  const token = new URLSearchParams(h).get("pago")
  return token && token.length >= 8 ? { kind: "paid", token } : null
}

/**
 * Lo que la tienda necesita al volver y que el servidor no devuelve: el total
 * mostrado, el horario, la dirección de entrega… (la confirmación los pinta tal
 * cual los vio el cliente) y el carrito, por si cancela.
 */
export type PaymentHandoffStash = {
  token: string
  slug: string
  cart: CartItem[]
  total: number
  scheduledFor: string | null
  deliveryAddress: string | null
  deliveryEtaSeconds: number | null
  expectWelcomeCoupon: boolean
  /** Sesión de sugerencias (PR 5c) para enlazar el pedido al volver; ausente si la sesión no vio tarjetas o el stash es viejo. */
  suggestionSessionId?: string
  /** Embudo del checkout (spec 2026-10-19 §5.9): el `paid` se manda al volver, con la misma sesión. */
  funnel?: {
    sessionId: string
    path: "v1" | "v2"
    fulfillment: "pickup" | "delivery" | "ship" | "table"
    scheduled: boolean
    /** Lo que se cobra (total + donación), para el control mostrado vs cobrado. */
    chargedTotal: number
  }
  savedAt: number
}

const STASH_KEY = "redy:pagoDePaso"
/** Más que la vida de una sesión de North: pasado esto el stash es basura. */
const STASH_TTL_MS = 2 * 60 * 60_000

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">

function storage(): StorageLike | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null
  } catch {
    return null
  }
}

export function saveHandoffStash(stash: PaymentHandoffStash, store: StorageLike | null = storage()): void {
  try {
    store?.setItem(STASH_KEY, JSON.stringify(stash))
  } catch {
    // Sin storage el pago igual funciona; solo se pierde el carrito si cancela.
  }
}

/** El stash de ESTE negocio (y, si se pide, de ESTE token), vigente; o null. */
export function readHandoffStash(
  slug: string,
  token: string | null,
  store: StorageLike | null = storage(),
  now: number = Date.now()
): PaymentHandoffStash | null {
  try {
    const raw = store?.getItem(STASH_KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as PaymentHandoffStash
    if (s.slug !== slug || !Array.isArray(s.cart) || typeof s.token !== "string") return null
    if (token !== null && s.token !== token) return null
    if (typeof s.savedAt !== "number" || now - s.savedAt > STASH_TTL_MS) return null
    return s
  } catch {
    return null
  }
}

export function clearHandoffStash(store: StorageLike | null = storage()): void {
  try {
    store?.removeItem(STASH_KEY)
  } catch {
    /* nada que limpiar */
  }
}

/** Lo que devuelve /api/online-orders/intent-status (solo lo que se usa aquí). */
export type IntentStatusResponse = {
  status?: string
  order?: { id: string; order_number: string | null }
  walletSaveUrl?: string | null
  accountClaimUrl?: string | null
  trackingUrl?: string | null
  /** Sustituciones (spec 2026-09-28 §4): best-effort, solo con el flag del local. */
  substitutionPref?: string
  substitutionContactAllowed?: boolean
  /** Valor de la compra para el pixel (orders.subtotal), calculado en el servidor. */
  conversion?: unknown
}

/**
 * Confirmación a partir del intent resuelto + lo que se guardó al salir. Sin
 * stash (volvió en otra pestaña) se confirma igual: la orden es real y el
 * recibo/seguimiento funcionan; solo el total no se puede pintar.
 */
export function confirmedOrderFromIntent(
  token: string,
  data: IntentStatusResponse,
  stash: PaymentHandoffStash | null,
  welcomeCoupon: WelcomeCoupon | null
): ConfirmedOrder | null {
  if (data.status !== "fulfilled" || !data.order) return null
  const substitutionPref: SubstitutionPref | undefined = isSubstitutionPref(data.substitutionPref)
    ? data.substitutionPref
    : undefined
  return {
    id: data.order.id,
    number: orderPaymentReference(data.order.id, data.order.order_number),
    total: stash?.total ?? 0,
    scheduledFor: stash?.scheduledFor ?? null,
    receiptToken: token,
    walletSaveUrl: data.walletSaveUrl ?? null,
    accountClaimUrl: data.accountClaimUrl ?? null,
    trackingUrl: data.trackingUrl ?? null,
    welcomeCoupon,
    deliveryAddress: stash?.deliveryAddress ?? null,
    deliveryEtaSeconds: stash?.deliveryEtaSeconds ?? null,
    substitutionPref,
    substitutionContactAllowed: data.substitutionContactAllowed,
    // El carrito no vuelve del servidor: lo que se vio ANTES de pagar es lo
    // único que hay (mismo criterio que OrderSheet.onSuccess, closure previo
    // al reset), por índice — un aviso `cart_index >= 0` necesita el nombre.
    confirmedCartNames: (stash?.cart ?? []).map((c) => c.name),
    conversion: parseConversion(data.conversion),
  }
}
